import {
  computed,
  DOCUMENT,
  ElementRef,
  EventEmitter,
  inject,
  Injectable,
  Output,
  signal,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core'
import { MatDrawerToggleResult, MatSidenav } from '@angular/material/sidenav'
import { Observable } from 'rxjs/internal/Observable'
import { PageScrollService, PageScrollTarget } from 'ngx-page-scroll-core'

@Injectable({
  providedIn: 'root',
})
export class SidebarService {
  /** Event that triggers when close action is discarded because it is disabled */
  @Output() public closeDiscarded: EventEmitter<null> = new EventEmitter()

  /** If opened as obs for view usage */
  public sidebarOpened$: Observable<boolean>

  /** If closed is triggered */
  public sidebarClosedStart$: Observable<void>

  public sideNavMode = signal<'over' | 'side'>('over')

  public withBackdrop = computed(() => this.sideNavMode() === 'over')

  protected _pageScrollService = inject(PageScrollService)

  protected _document = inject<Document>(DOCUMENT)

  /** Instance of the mat side nav */
  private sidebar: MatSidenav

  /** Instance of the mat side nav ref */
  private sidebarRef: ElementRef

  /** Instance of container ref */
  private vcf: ViewContainerRef

  /** Whether or not we could close the sidebar  */
  private isEnableClose = true

  /** Sets to the sidebar
   * @param { MatSidenav } sidenav the instance of the mat sidenav
   */
  public setSidebar(sidenav: MatSidenav): void {
    this.sidebar = sidenav
    this.sidebarOpened$ = this.sidebar.openedChange.asObservable()

    this.sidebarClosedStart$ = this.sidebar.closedStart
  }

  /** Sets to the sidebar ref
   * @param { ElementRef } sidenavRef the instance of the mat sidenav ref
   */
  public setSidebarRef(sidenavRef: ElementRef): void {
    this.sidebarRef = sidenavRef
  }

  /** Sets to the container
   * @param { ViewContainerRef } viewContainerRef the instance of container ref
   */
  public setContentVcf(viewContainerRef: ViewContainerRef): void {
    this.vcf = viewContainerRef
  }

  /** Create an anchor of the embedded view
   * @param { TemplateRef } template ref to copy
   */
  public createView<T>(template: TemplateRef<T>): void {
    this.vcf.clear()
    this.vcf.createEmbeddedView(template)
  }

  /** Opens the sidebar
   * @param { TemplateRef } template ref to copy
   */
  public async open<T>(
    template: TemplateRef<T>
  ): Promise<MatDrawerToggleResult> {
    this.createView(template)
    return this.sidebar.open()
  }

  /** Closes the sidebar */
  public async close(): Promise<MatDrawerToggleResult | void> {
    if (this.isEnableClose) {
      this.vcf.clear()
      return this.sidebar.close()
    }

    this.closeDiscarded.emit()
  }

  /** Opens the sidebar */
  public async toggle(): Promise<MatDrawerToggleResult> {
    return this.sidebar.toggle()
  }

  /** Authorized or not the close sidebar action */
  public enableClose(val: boolean): void {
    this.isEnableClose = val
  }

  /** Used to scroll in the sidebar to the target
   * @param { PageScrollTarget } scrollTarget the target to reach
   */
  public scrollTo(scrollTarget: PageScrollTarget): void {
    const viewTarget = this.sidebarRef.nativeElement.children[0]

    if (viewTarget) {
      const scrollInstance = this._pageScrollService.create({
        document: this._document,
        scrollTarget,
        scrollViews: [viewTarget],
      })

      this._pageScrollService.start(scrollInstance)
    }
  }
}
